import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { ChevronLeft, ChevronRight, Users, Clock, Calendar, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { shouldUsePinMode } from '@/utils/pinModeUtils';

type Shift = {
  id: string;
  resourceId: string;
  start: string;
  end?: string | null;
  projectId?: string | null;
  project?: string | null;
  description?: string | null;
};

const minutesBetween = (start: string, end?: string | null) => {
  if (!end) return 0;
  const diff = (new Date(end).getTime() - new Date(start).getTime()) / 60000;
  return Math.max(0, Math.round(diff));
};

const fmtHM = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h${m.toString().padStart(2, '0')}`;
};

const formatWeekRange = (d: Date) => {
  const start = new Date(d);
  const day = start.getDay();
  const diff = start.getDate() - day + (day === 0 ? -6 : 1); // lundi
  start.setDate(diff);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return {
    start,
    end,
    label: `${start.toLocaleDateString('fr-CA')} - ${end.toLocaleDateString('fr-CA')}`,
  };
};

const toDateInput = (value: string) => new Date(value).toLocaleDateString('fr-CA');

const toTimeInput = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '00:00';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const isProjectActive = (project: { status?: string | null; is_active?: boolean | null }) => {
  if (typeof project.is_active === 'boolean') return project.is_active;
  if (project.status) return project.status === 'active';
  return true;
};

export const TimesheetsPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [anchorDate, setAnchorDate] = useState<Date>(new Date());
  const [profilesList, setProfilesList] = useState<{ id: string; first_name: string; last_name: string }[]>([]);
  const [selectedResource, setSelectedResource] = useState<string>(() => params.get('resource') || 'all');
  const [projectsCatalog, setProjectsCatalog] = useState<Array<{ id: string; name: string; status?: string | null; is_active?: boolean | null }>>([]);
  const [isPinMode, setIsPinMode] = useState(false);
  const [managerPin, setManagerPin] = useState<string | null>(null);
  const [editingShiftId, setEditingShiftId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('09:00');
  const [editEndTime, setEditEndTime] = useState('17:00');
  const [editProjectId, setEditProjectId] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const doLoad = async () => {
      setLoading(true);
      setError(null);
      try {
        if (params.get('error') === 'true') throw new Error('Network');
        const { start, end } = formatWeekRange(anchorDate);
        if (params.get('empty') === 'true') { setShifts([]); return; }
        const managerPin = typeof window !== 'undefined' ? window.localStorage.getItem('manager_pin') : null;
        const usePinMode = shouldUsePinMode();
        setIsPinMode(usePinMode);
        setManagerPin(managerPin);
        if (usePinMode && !managerPin) {
          throw new Error('PIN manager manquant');
        }

        // 1) time_entries sur la plage demandée
        const { data: timeEntries, error: teErr } = usePinMode
          ? await supabase.rpc('pin_list_time_entries_range', {
              p_pin: managerPin,
              p_start: start.toISOString(),
              p_end: end.toISOString(),
              p_limit: 2000,
              p_offset: 0,
              p_order: 'asc',
            })
          : await supabase
              .from('time_entries')
              .select('id, user_id, project_id, start_time, end_time, duration_minutes, description')
              .gte('start_time', start.toISOString())
              .lte('start_time', end.toISOString())
              .not('end_time', 'is', null)
              .order('start_time', { ascending: true });
        if (teErr) throw teErr;

        const entries = timeEntries || [];
        const userIds = Array.from(new Set(entries.map(e => e.user_id)));

        // 2) Hydrater profils
        const [profilesRes, projectsRes] = await Promise.all([
          usePinMode
            ? supabase.rpc('pin_list_profiles', { p_pin: managerPin })
            : userIds.length > 0
                ? supabase.from('profiles').select('id, first_name, last_name').in('id', userIds)
                : Promise.resolve({ data: [], error: null } as any),
          usePinMode
            ? supabase.rpc('pin_list_projects', { p_pin: managerPin })
            : supabase.from('projects').select('id, name, status, is_active').order('name'),
        ]);
        if (profilesRes.error) throw profilesRes.error;
        if (projectsRes.error) throw projectsRes.error;

        const projectMap = new Map<string, string>((projectsRes.data || []).map((p: any) => [p.id, p.name]));
        const profileMap = new Map<string, { first_name: string; last_name: string }>((profilesRes.data || []).map((p: any) => [p.id, { first_name: p.first_name, last_name: p.last_name }]));
        setProfilesList((profilesRes.data || []) as any);
        setProjectsCatalog((projectsRes.data || []) as any);

        // 3) Adapter pour l'affichage
        const view: Shift[] = entries.map((e: any) => ({
          id: e.id,
          resourceId: e.user_id,
          start: e.start_time,
          end: e.end_time,
          projectId: e.project_id ?? null,
          project: projectMap.get(e.project_id) || null,
          description: e.description ?? null,
        }));

        setShifts(view);
      } catch (e: any) {
        setError('Impossible de charger les feuilles de temps');
      } finally {
        setLoading(false);
      }
    };
    void doLoad();
  }, [params, anchorDate]);

  // Nom complet d'une ressource
  const getResourceName = (rid: string) => {
    const p = profilesList.find(p => p.id === rid);
    if (!p) return rid;
    const first = p.first_name?.trim() || '';
    const last = p.last_name?.trim() || '';
    const name = `${first} ${last}`.trim();
    return name || rid;
  };

  const byResource = useMemo(() => {
    const map = new Map<string, Shift[]>();
    shifts.forEach(s => {
      const arr = map.get(s.resourceId) || [];
      arr.push(s);
      map.set(s.resourceId, arr);
    });
    return map;
  }, [shifts]);

  const resourceTotal = (rid: string) => {
    const arr = byResource.get(rid) || [];
    return arr.reduce((sum, s) => sum + minutesBetween(s.start, s.end), 0);
  };

  const weekTotal = Array.from(byResource.keys()).reduce((sum, rid) => sum + resourceTotal(rid), 0);
  const activeProjects = useMemo(() => projectsCatalog.filter(isProjectActive), [projectsCatalog]);
  const editingShift = useMemo(
    () => shifts.find((shift) => shift.id === editingShiftId) || null,
    [editingShiftId, shifts]
  );
  const availableProjects = useMemo(() => {
    if (!editingShift?.projectId) return activeProjects;
    if (activeProjects.some(project => project.id === editingShift.projectId)) return activeProjects;
    const current = projectsCatalog.find(project => project.id === editingShift.projectId);
    return current ? [...activeProjects, current] : activeProjects;
  }, [activeProjects, editingShift, projectsCatalog]);
  const getProjectNameById = (projectId?: string | null) => {
    if (!projectId) return null;
    return projectsCatalog.find(project => project.id === projectId)?.name ?? null;
  };

  const startEdit = (shift: Shift) => {
    setEditingShiftId(shift.id);
    setEditDate(toDateInput(shift.start));
    setEditStartTime(toTimeInput(shift.start));
    setEditEndTime(toTimeInput(shift.end ?? shift.start));
    setEditProjectId(shift.projectId ?? '');
    setEditDescription(shift.description ?? '');
  };

  const cancelEdit = () => {
    setEditingShiftId(null);
    setEditDate('');
    setEditStartTime('09:00');
    setEditEndTime('17:00');
    setEditProjectId('');
    setEditDescription('');
  };

  const updateShift = async (
    shift: Shift,
    values: {
      startDateTime: Date;
      endDateTime: Date;
      projectId: string;
      description: string;
    }
  ) => {
    const durationMinutes = Math.round((values.endDateTime.getTime() - values.startDateTime.getTime()) / 60000);
    const updatePayload = {
      start_time: values.startDateTime.toISOString(),
      end_time: values.endDateTime.toISOString(),
      duration_minutes: durationMinutes,
      description: values.description.trim() ? values.description.trim() : null,
      project_id: values.projectId,
    };
    if (isPinMode && !managerPin) {
      throw new Error('PIN manager manquant');
    }
    const { error: updateError } = isPinMode
      ? await supabase.rpc('pin_update_time_entry_for_employee', {
          p_pin: managerPin,
          p_entry_id: shift.id,
          p_start_time: updatePayload.start_time,
          p_end_time: updatePayload.end_time,
          p_duration_minutes: updatePayload.duration_minutes,
          p_description: updatePayload.description,
          p_project_id: updatePayload.project_id,
          p_status: 'closed',
        })
      : await supabase
          .from('time_entries')
          .update(updatePayload)
          .eq('id', shift.id);
    if (updateError) throw updateError;

    const projectName = getProjectNameById(values.projectId);
    setShifts(prev => prev.map(item => item.id === shift.id
      ? {
          ...item,
          start: updatePayload.start_time,
          end: updatePayload.end_time,
          projectId: values.projectId,
          project: projectName,
          description: updatePayload.description,
        }
      : item
    ));
  };

  const handleSaveEdit = async () => {
    if (!editingShift) return;
    if (!editProjectId) {
      toast({
        title: "Projet requis",
        description: "Sélectionnez un projet pour mettre à jour l'entrée.",
        variant: "destructive",
      });
      return;
    }

    const startDateTime = new Date(`${editDate}T${editStartTime}:00`);
    const endDateTime = new Date(`${editDate}T${editEndTime}:00`);
    if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
      toast({
        title: "Date/heure invalide",
        description: "Vérifiez la date et les heures saisies.",
        variant: "destructive",
      });
      return;
    }
    if (endDateTime <= startDateTime) {
      toast({
        title: "Heures invalides",
        description: "L'heure de fin doit être après l'heure de début.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSavingEdit(true);
      await updateShift(editingShift, {
        startDateTime,
        endDateTime,
        projectId: editProjectId,
        description: editDescription,
      });
      toast({
        title: "Entrée mise à jour",
        description: "Les modifications ont été enregistrées.",
      });
      cancelEdit();
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour l'entrée de temps.",
        variant: "destructive",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4 flex items-center justify-center">
        <div data-testid="timesheet-loading" className="text-lg">Chargement des feuilles de temps…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-6">
              <div data-testid="timesheet-error" className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold">Une erreur est survenue</div>
                  <div className="text-muted-foreground">{error}</div>
                </div>
                <Button data-testid="retry" onClick={() => window.location.reload()}>Réessayer</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6" data-testid="timesheet-page">
        {/* Bouton de retour */}
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="h-6 w-6 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Feuilles de temps</h1>
              <p className="text-muted-foreground">Shifts par ressource et total hebdomadaire</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              data-testid="week-nav-prev"
              onClick={() => setAnchorDate(d => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 7))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Badge variant="secondary" data-testid="week-range">{formatWeekRange(anchorDate).label}</Badge>
            <Button
              variant="outline"
              size="icon"
              data-testid="week-nav-next"
              onClick={() => setAnchorDate(d => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filtre Ressource */}
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground">Filtrer par ressource</div>
          <Select
            value={selectedResource}
            onValueChange={(val) => {
              setSelectedResource(val);
              const sp = new URLSearchParams(params);
              if (val === 'all') {
                sp.delete('resource');
              } else {
                sp.set('resource', val);
              }
              window.history.replaceState(null, '', `?${sp.toString()}`);
            }}
          >
            <SelectTrigger className="w-[260px]" data-testid="resource-filter">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {profilesList.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {(p.first_name || '') + ' ' + (p.last_name || '')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-1">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-blue-600" />
                <div>
                  <div className="text-sm text-muted-foreground">Ressources</div>
                  <div className="text-xl font-bold">{byResource.size}</div>
                </div>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-green-600" />
                <div>
                  <div className="text-sm text-muted-foreground">Total semaine</div>
                  <div data-testid="hours-week-total" className="text-xl font-bold">{fmtHM(weekTotal)}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {shifts.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <div data-testid="timesheet-empty" className="text-muted-foreground">Aucun shift cette semaine</div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="resources-table">
            {(selectedResource === 'all' ? Array.from(byResource.keys()) : Array.from(byResource.keys()).filter(r => r === selectedResource)).map((rid) => (
              <Card key={rid} data-testid={`resource-row-${rid}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">
                    <span data-testid={`resource-name-${rid}`} className="font-semibold">Ressource:</span> {getResourceName(rid)}
                  </CardTitle>
                  <Badge data-testid={`resource-week-total-${rid}`}>{fmtHM(resourceTotal(rid))}</Badge>
                </CardHeader>
                <CardContent>
                  <Table data-testid={`shifts-table-${rid}`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Projet</TableHead>
                        <TableHead className="text-right">Durée</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(byResource.get(rid) || []).map(s => (
                        <React.Fragment key={s.id}>
                          <TableRow data-testid={`shift-row-${s.id}`}>
                            <TableCell data-testid={`shift-date-${s.id}`}>{new Date(s.start).toLocaleDateString('fr-CA')}</TableCell>
                            <TableCell data-testid={`shift-project-${s.id}`}>{s.project ?? '-'}</TableCell>
                            <TableCell className="text-right" data-testid={`shift-duration-${s.id}`}>{fmtHM(minutesBetween(s.start, s.end))}</TableCell>
                            <TableCell className="text-right">
                              {editingShiftId === s.id ? (
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    type="button"
                                    disabled={savingEdit}
                                    onClick={cancelEdit}
                                  >
                                    Annuler
                                  </Button>
                                  <Button
                                    size="sm"
                                    type="button"
                                    disabled={savingEdit}
                                    onClick={handleSaveEdit}
                                  >
                                    Enregistrer
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  type="button"
                                  onClick={() => startEdit(s)}
                                  data-testid={`shift-edit-${s.id}`}
                                >
                                  Modifier
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                          {editingShiftId === s.id && (
                            <TableRow>
                              <TableCell colSpan={4} className="bg-muted/20">
                                <div className="grid gap-4 md:grid-cols-2">
                                  <div className="grid gap-2">
                                    <Label htmlFor={`edit-project-${s.id}`}>Projet</Label>
                                    <Select value={editProjectId} onValueChange={setEditProjectId}>
                                      <SelectTrigger id={`edit-project-${s.id}`}>
                                        <SelectValue placeholder="Sélectionner un projet" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {availableProjects.map((project) => (
                                          <SelectItem key={project.id} value={project.id}>
                                            {project.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="grid gap-2">
                                    <Label htmlFor={`edit-date-${s.id}`}>Date</Label>
                                    <Input
                                      id={`edit-date-${s.id}`}
                                      type="date"
                                      value={editDate}
                                      onChange={(event) => setEditDate(event.target.value)}
                                    />
                                  </div>
                                  <div className="grid gap-2">
                                    <Label htmlFor={`edit-start-${s.id}`}>Heure de début</Label>
                                    <Input
                                      id={`edit-start-${s.id}`}
                                      type="time"
                                      value={editStartTime}
                                      onChange={(event) => setEditStartTime(event.target.value)}
                                    />
                                  </div>
                                  <div className="grid gap-2">
                                    <Label htmlFor={`edit-end-${s.id}`}>Heure de fin</Label>
                                    <Input
                                      id={`edit-end-${s.id}`}
                                      type="time"
                                      value={editEndTime}
                                      onChange={(event) => setEditEndTime(event.target.value)}
                                    />
                                  </div>
                                  <div className="grid gap-2 md:col-span-2">
                                    <Label htmlFor={`edit-description-${s.id}`}>Description</Label>
                                    <Textarea
                                      id={`edit-description-${s.id}`}
                                      value={editDescription}
                                      onChange={(event) => setEditDescription(event.target.value)}
                                      placeholder="Décrivez le travail effectué..."
                                    />
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimesheetsPage;


